import { observer } from 'src/utils/mobx-react';
import React, { ChangeEvent, useState } from 'react';
import { AppState, useAppStateContext } from 'src/AppState/AppState';
import { computed, makeAutoObservable } from 'mobx';
import { SportCompetitionsState } from 'src/components/SportPopup/SportCompetitions/SportCompetitionsState';
import { CompetitionId } from 'src_common/common/websocket2/id/WebsocketId';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { Resource, Result } from 'src_common/common/mobx-utils/Resource';
import { OpenapiProxyStaffGetCompetitionByIdResponse200Type } from 'src/api_openapi/generated/openapi_proxy_staff_get_competition_by_id';
import { GradeType } from 'src/domains/sportsbook/trading/sdk/models/grades/gradeModels';
import { Response200Type } from 'src/api_openapi/generated/openapi_socket_api_staff_tag_list';
type SimpleSelectInputPropsType = {
    label: string;
    dataTest: string;
    name: string;
    options: Array<GradeType>;
    current?: string | number | null;
    onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
    competitionName: string;
};

export type SingleCompetitionType = OpenapiProxyStaffGetCompetitionByIdResponse200Type;

const SimpleSelectInput = observer('SimpleSelectInput', (props: SimpleSelectInputPropsType) => {
    return (
        <div className='element hasLabel'>
            <div
                className='select-wrapper'
                data-test={props.dataTest}
            >
                {/* data-test-initial-id needed for test automation - problems with invoke value of selected id (C15801) */}
                <select
                    name={props.name}
                    defaultValue={
                        props.current === null || props.current === undefined ? undefined : props.current.toString()
                    }
                    onChange={props.onChange}
                    data-test-initial-grade-id={props.current}
                >
                    {props.options.map((item) => (
                        <option
                            key={item.id}
                            value={item.id}
                        >
                            {item.displayName === null || item.displayName === undefined ? item.name : item.displayName}
                        </option>
                    ))}
                </select>
                <label>{props.label}</label>
            </div>
        </div>
    );
});

type TagForViewType = {
    id: string;
    label: string;
    values: Array<{ id: string; isDefault: boolean; label: string }>;
    current: string | undefined;
};
class CompetitionSettingsFormState {
    public readonly currentCompetition: Resource<SingleCompetitionType>;

    public constructor(
        private readonly appState: AppState,
        private readonly sportCompetitionsState: SportCompetitionsState,
        private readonly sportId: string,
        private readonly competitionId: CompetitionId
    ) {
        this.currentCompetition = new Resource(async (): Promise<SingleCompetitionType> => {
            const idV2 = await competitionId.idv2();
            return await appState.sdkStaff.getCompetitionById({
                requestBody: {
                    id: competitionId.toOldId(),
                    idV2,
                },
            });
        });
        makeAutoObservable(this);
    }

    public get competition(): SingleCompetitionType | 'loading' {
        const competition = this.currentCompetition.get();
        if (competition.type === 'ready') {
            return competition.value;
        }

        return 'loading';
    }

    @computed private get tags(): Result<Response200Type> {
        return this.appState.sdkStaff.platformTags.get();
    }

    @computed public get tagsForCompetition(): Array<TagForViewType> {
        const allTags = this.tags;

        if (allTags.type !== 'ready') {
            return [];
        }

        const competition = this.competition;
        if (competition === 'loading') {
            return [];
        }

        const tags: Array<TagForViewType> = [];
        const tagsInCompetition = competition.tags;

        for (const tagKey in allTags.value.definitions) {
            if (tagsInCompetition[tagKey] === undefined) {
                const defTag = allTags.value.definitions[tagKey];

                if (defTag !== undefined) {
                    if (defTag.applicableTo.includes('competition') && defTag.sports.includes(this.sportId)) {
                        const tag = {
                            id: tagKey,
                            label: defTag.label,
                            values: defTag.values,
                            current: tagsInCompetition[tagKey],
                        };
                        tags.push(tag);
                    }
                }
            } else {
                const tagFromList = allTags.value.definitions[tagKey];
                const tag = {
                    id: tagKey,
                    label: tagFromList?.label ?? '',
                    values: tagFromList?.values ?? [],
                    current: tagsInCompetition[tagKey],
                };
                tags.push(tag);
            }
        }

        return tags;
    }

    public onTagChange = async (tagKey: string, tagValue: string): Promise<void> => {
        const idV2 = await this.competitionId.idv2();
        await this.appState.sdkStaff.patchCompetition({
            sport_id: this.sportId,
            competition_id: this.competitionId.toOldId(),
            requestBody: {
                idV2,
                tags: {
                    [tagKey]: tagValue,
                },
            },
        });

        await this.currentCompetition.refresh();
    };

    public onAutoTakeDownChange = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
        const autoTakeDown = e.target.checked;
        const idV2 = await this.competitionId.idv2();
        await this.appState.sdkStaff.patchCompetition({
            sport_id: this.sportId,
            competition_id: this.competitionId.toOldId(),
            requestBody: {
                idV2,
                autoTakeDown,
            },
        });
        await this.currentCompetition.refresh();
    };

    public onPrematchChange = async (e: ChangeEvent<HTMLSelectElement>): Promise<void> => {
        await this.sportCompetitionsState.onGradeChange('prematch', e.target.value, this.competitionId);
        await this.currentCompetition.refresh();
    };

    public onShowChange = async (e: ChangeEvent<HTMLSelectElement>): Promise<void> => {
        await this.sportCompetitionsState.onGradeChange('show', e.target.value, this.competitionId);
        await this.currentCompetition.refresh();
    };

    public onInPlayChange = async (e: ChangeEvent<HTMLSelectElement>): Promise<void> => {
        await this.sportCompetitionsState.onGradeChange('inPlay', e.target.value, this.competitionId);
        await this.currentCompetition.refresh();
    };

    public onChangeName = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
        const newName = e.target.value;
        const idV2 = await this.competitionId.idv2();
        await this.appState.sdkStaff.patchCompetition({
            sport_id: this.sportId,
            competition_id: this.competitionId.toOldId(),
            requestBody: {
                idV2,
                name: newName,
            },
        });

        await this.currentCompetition.refresh();
        await this.sportCompetitionsState.refreshCompetitions();
    };

    public onChangeInPlayDelay = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
        const value = e.target.value;
        // eslint-disable-next-line no-restricted-globals
        const inPlayDelay = Number(e.target.value);
        const idV2 = await this.competitionId.idv2();

        const inPlayDelaySend = value.length === 0 ? null : inPlayDelay;

        await this.appState.sdkStaff.patchCompetition({
            sport_id: this.sportId,
            competition_id: this.competitionId.toOldId(),
            requestBody: {
                idV2,
                inPlayDelay: inPlayDelaySend,
            },
        });
        await this.currentCompetition.refresh();
    };
}

class CompetitionSettingAutoMap {
    private readonly data: MobxMapAutoNew<CompetitionId, CompetitionSettingsFormState>;

    public constructor(appState: AppState, sportId: string, sportCompetitionsState: SportCompetitionsState) {
        this.data = new MobxMapAutoNew(
            (id) => new CompetitionSettingsFormState(appState, sportCompetitionsState, sportId, id)
        );
    }

    public get(id: CompetitionId): CompetitionSettingsFormState {
        return this.data.get(id);
    }
}

type CompetitionSettingsFormPropsType = {
    sportCompetitionsState: SportCompetitionsState;
    sportId: string;
};

export const CompetitionSettingsForm = observer(
    'CompetitionSettingsForm',
    (props: CompetitionSettingsFormPropsType) => {
        const appState = useAppStateContext();
        const [stateMap] = useState(
            () => new CompetitionSettingAutoMap(appState, props.sportId, props.sportCompetitionsState)
        );

        const openedCompetition = props.sportCompetitionsState.openedCompetition;
        const grades = props.sportCompetitionsState.grades;

        if (openedCompetition === null) {
            return null;
        }

        const state = stateMap.get(openedCompetition);
        const competition = state.competition;

        if (competition === 'loading') {
            return <div className='row'>Loading...</div>;
        }

        const inPlayDelay =
            competition.inPlayDelay === undefined || competition.inPlayDelay === null
                ? undefined
                : competition.inPlayDelay;

        return (
            <div className='row'>
                <div className='col-6'>
                    <h4>Settings</h4>
                    {/*TODO: the key is temporary, instead the input should has own state*/}
                    <div
                        className='element required hasLabel'
                        key={competition.name}
                    >
                        <input
                            type='text'
                            name='name'
                            defaultValue={competition.name}
                            onBlur={state.onChangeName}
                            data-test='competition-name'
                        />
                        <label>Name</label>
                    </div>
                    {/*TODO: input should has own state instead of key*/}
                    <SimpleSelectInput
                        key={`prematch-${competition.grades?.prematch?.id}`}
                        label='Prematch grade'
                        dataTest='prematch-grade'
                        name='grades.prematch.id'
                        options={grades}
                        current={competition.grades?.prematch?.id}
                        onChange={state.onPrematchChange}
                        competitionName={competition.name}
                    />

                    {/*TODO: input should has own state instead of key*/}
                    <SimpleSelectInput
                        key={`show-${competition.grades?.show?.id}`}
                        label='Show grade'
                        dataTest='show-grade'
                        name='grades.show.id'
                        options={grades}
                        current={competition.grades?.show?.id}
                        onChange={state.onShowChange}
                        competitionName={competition.name}
                    />

                    {/*TODO: input should has own state instead of key*/}
                    <SimpleSelectInput
                        key={`in-play-${competition.grades?.inPlay?.id}`}
                        label='In-play grade'
                        dataTest='in-play-grade'
                        name='grades.inPlay.id'
                        options={grades}
                        current={competition.grades?.inPlay?.id}
                        onChange={state.onInPlayChange}
                        competitionName={competition.name}
                    />

                    <div
                        className='element hasLabel'
                        key={`in-play-delay-${competition.name}-${inPlayDelay}`}
                    >
                        <input
                            type='text'
                            name='inPlayDelay'
                            defaultValue={inPlayDelay}
                            onBlur={state.onChangeInPlayDelay}
                            data-test='in-play-delay'
                        />
                        <label>In-play delay</label>
                    </div>

                    <div
                        className='element checkbox'
                        data-test='take-down-automatically'
                    >
                        <label>
                            <input
                                type='checkbox'
                                defaultChecked={competition.autoTakeDown}
                                onChange={state.onAutoTakeDownChange}
                            />
                            <span>Take down automatically</span>
                        </label>
                    </div>
                </div>

                <div className='col-6'>
                    <h4>Tags</h4>

                    <table>
                        <thead>
                            <tr>
                                <th>Tag name</th>
                                <th>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.tagsForCompetition.map((tag) => {
                                const _tagName = `tags.${tag.id}.0`;
                                return (
                                    <tr key={`${competition.id}-${tag.id}`}>
                                        <td>{tag.label}</td>
                                        <td>
                                            <div className='element'>
                                                <div className='select-wrapper'>
                                                    <select
                                                        name={_tagName}
                                                        onChange={async (
                                                            e: ChangeEvent<HTMLSelectElement>
                                                        ): Promise<void> => {
                                                            await state.onTagChange(tag.id, e.target.value);
                                                        }}
                                                        defaultValue={tag.current}
                                                    >
                                                        {tag.values.map((value) => {
                                                            return (
                                                                <option
                                                                    key={value.id}
                                                                    value={value.id}
                                                                >
                                                                    {value.label}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
);
